// libs
import { motion, Variants } from "framer-motion";
import { useMedia } from "react-use";
import styled, { css, useTheme } from "styled-components";
// components
import { Box } from "../Box";
import Button from "../Button";
import Icon from "../Icon";
import IconRating from "../IconRating";
import { Text } from "../Text";
import TextArea from "../TextArea";
// other
import { easings } from "../../styles/utils";

type FeedbackProps = {
  open: boolean;
  onToggleClick: () => any;
};

const StyledRoot = styled(motion.div)`
  position: absolute;
  z-index: 1;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blueDark};
  color: ${({ theme }) => theme.colors.white};
  padding: 24px 16px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      top: 0;
      padding: 40px;
      left: auto;
      right: 0;
      width: 424px;
      min-height: 80%;
    }
  `}
`;

const ButtonPosition = styled.div`
  position: absolute;
  bottom: 100%;
  left: 24px;
  z-index: -1;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      bottom: 0;
      left: 0;
      transform: rotate(-90deg);
      transform-origin: bottom left;
    }
  `}
`;

const FeedbackButton = styled(motion.button)`
  background-color: ${({ theme }) => theme.colors.blueDark};
  color: inherit;
  border: none;
  line-height: 48px;
  padding: 0 32px;
  border-radius: 24px 24px 0 0;
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  svg {
    height: 1.2em;
    width: 1.2em;
  }

  svg:first-child {
    margin-right: 6px;
    margin-left: -6px;

    ${({ theme }) => css`
      ${theme.mediaQueries.md} {
        transform: rotate(90deg);
      }
    `}
  }
  svg:last-child {
    margin-left: 6px;
    margin-right: -6px;
  }
  &::after {
    content: "";
    position: absolute;
    top: calc(100% - 1px);
    left: 0;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.blueDark};
    height: 10px;
  }
`;

const rootVariants: Variants = {
  open: (isDesktop) =>
    isDesktop
      ? { x: 0, y: 0, transition: { ease: easings.easeOutCubic } }
      : { x: 0, y: "-100%", transition: { ease: easings.easeOutCubic } },
  closed: (isDesktop) =>
    isDesktop
      ? { x: "100%", y: 0, transition: { ease: easings.easeOutCubic } }
      : { x: 0, y: 0, transition: { ease: easings.easeOutCubic } },
};

export default function Feedback({ open, onToggleClick }: FeedbackProps) {
  const theme = useTheme();
  const isDesktop = useMedia(`(min-width: ${theme.breakpoints[1]})`);

  console.log(isDesktop);
  return (
    <StyledRoot
      animate={open ? "open" : "closed"}
      variants={rootVariants}
      custom={isDesktop}
    >
      <ButtonPosition>
        <FeedbackButton whileHover={{ y: -4 }} onClick={onToggleClick}>
          <Icon icon="rateReview" />
          Tell us what you think
          {open ? <Icon icon="expandMore" /> : <Icon icon="expandLess" />}
        </FeedbackButton>
      </ButtonPosition>
      <Text variant="h4">How was your experience?</Text>
      <Box mt={[3]} mb={[3, null, 4]}>
        <IconRating />
      </Box>
      <Text fontWeight="700">
        Are there any comments you have which could help us to make this
        experience better?
      </Text>
      <Box my={3}>
        <TextArea rows={4} />
      </Box>
      <Box mt={4} textAlign="right">
        <Button color="hotPink" icon="arrowForward">
          Submit feedback
        </Button>
      </Box>
    </StyledRoot>
  );
}
